import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { trackingAttrs } from "../../utils";

export type TrendDirection = "up" | "down" | "neutral";

type RootProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	action?: ReactNode;
	ref?: Ref<HTMLDivElement>;
};

const Root: FC<RootProps> = ({ trackingId, action, className, children, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(
			"relative rounded-lg border border-bc-default bg-surface-base shadow-sm flex flex-col gap-2 p-3 lg:p-4",
			className,
		)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{children}
		{action && <div className="absolute top-2 right-2">{action}</div>}
	</div>
);
Root.displayName = "MetricCard";

const labelVariants = cva("uppercase tracking-[0.6px] text-tertiary font-semibold", {
	variants: {
		size: {
			sm: "text-[8px] lg:text-[10px]",
			md: "text-[11px] leading-[16px]",
		},
	},
	defaultVariants: {
		size: "sm",
	},
});

type LabelProps = HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof labelVariants> & {
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

const Label: FC<LabelProps> = ({ size, trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(labelVariants({ size }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Label.displayName = "MetricCard.Label";

/** Sizes track `Text`'s `title`/`heading`/`display` steps so values sit on the shared type ramp. */
const valueVariants = cva("font-bold text-primary tracking-[-0.8px]", {
	variants: {
		size: {
			sm: "text-base leading-[22px]",
			md: "text-xl leading-[26px]",
			lg: "text-2xl leading-[30px]",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type ValueProps = HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof valueVariants> & {
		trackingId?: string;
		ref?: Ref<HTMLDivElement>;
	};

const Value: FC<ValueProps> = ({ size, trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(valueVariants({ size }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Value.displayName = "MetricCard.Value";

const trendVariants = cva(
	"inline-flex items-center text-[8px] lg:text-[11px] font-semibold tracking-[-0.2px]",
	{
		variants: {
			direction: {
				up: "text-confirm",
				down: "text-danger",
				neutral: "text-tertiary",
			},
		},
		defaultVariants: {
			direction: "neutral",
		},
	},
);

type TrendProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	direction: TrendDirection;
	ref?: Ref<HTMLDivElement>;
};

const Trend: FC<TrendProps> = ({ direction, trackingId, className, children, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(trendVariants({ direction }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{direction === "up" && <Icon type="arrow-small-up" size="micro" />}
		{direction === "down" && <Icon type="arrow-small-down" size="micro" />}
		{children}
	</div>
);
Trend.displayName = "MetricCard.Trend";

type CaptionProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Caption: FC<CaptionProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("text-xs font-semibold text-tertiary", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Caption.displayName = "MetricCard.Caption";

type ValueRowProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const ValueRow: FC<ValueRowProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("flex items-baseline gap-2", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
ValueRow.displayName = "MetricCard.ValueRow";

export const MetricCard = Object.assign(Root, {
	Label,
	Value,
	Trend,
	Caption,
	ValueRow,
});

export type MetricCardProps = RootProps;
export type MetricCardLabelProps = LabelProps;
export type MetricCardValueProps = ValueProps;
export type MetricCardTrendProps = TrendProps;
export type MetricCardCaptionProps = CaptionProps;
export type MetricCardValueRowProps = ValueRowProps;
